'use client';
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, HeartHandshake, Heart, Baby, ShieldCheck, Activity,
  Phone, MessageCircle, MapPin, ArrowLeft, Info, Star, ChevronRight
} from 'lucide-react';
import TopNav from '@/components/layout/TopNav';
import { membershipCategories, getMembershipPlanByCategory } from '@/data/memberships';
import type { MembershipCategory } from '@/data/memberships';
import { hospitalConfig } from '@/data/hospitalConfig';
import { centres } from '@/data/locations';
import { trackEvent } from '@/lib/analytics';

const iconMap: Record<string, React.ElementType> = {
  Users, HeartHandshake, Heart, Baby, ShieldCheck, Activity,
};

const colorMap: Record<string, { icon: string; bg: string; border: string }> = {
  blue: { icon: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/20' },
  amber: { icon: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/20' },
  pink: { icon: 'text-pink-400', bg: 'bg-pink-400/10', border: 'border-pink-400/20' },
  emerald: { icon: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/20' },
  teal: { icon: 'text-teal-400', bg: 'bg-teal-400/10', border: 'border-teal-400/20' },
  violet: { icon: 'text-violet-400', bg: 'bg-violet-400/10', border: 'border-violet-400/20' },
};

type MembershipStep = 'categories' | 'detail';

export default function MembershipsContent() {
  const [step, setStep] = useState<MembershipStep>('categories');
  const [selectedCategory, setSelectedCategory] = useState<MembershipCategory | null>(null);

  const handleCategorySelect = (cat: MembershipCategory) => {
    setSelectedCategory(cat);
    setStep('detail');
    trackEvent({ event: 'programme_viewed', source: `membership-${cat.id}` });
  };

  const handleBack = () => {
    setStep('categories');
    setSelectedCategory(null);
  };

  const plan = selectedCategory ? getMembershipPlanByCategory(selectedCategory.id) : null;

  return (
    <main className="journey-bg min-h-screen flex flex-col pb-24" role="main" aria-label="Health Memberships">
      <TopNav showBackButton backHref="/digital-reception-screen" backLabel="Back" />

      <div className="flex-1 flex flex-col items-center pt-24 px-4 md:px-8">
        <div className="w-full max-w-3xl">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="mb-8"
          >
            <p className="section-label mb-2">HEALTH MEMBERSHIPS</p>
            <h1 className="text-display-md text-white font-bold leading-tight mb-3">
              CONTINUOUS<br />
              <span className="gold-gradient-text">CARE.</span>
            </h1>
            <p className="text-muted-foreground text-sm max-w-lg leading-relaxed">
              A Health Membership may help you stay on top of your care. Explore programmes designed around your needs.
            </p>
          </motion.div>

          <AnimatePresence mode="wait">
            {/* STEP: Categories */}
            {step === 'categories' && (
              <motion.div
                key="categories"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
              >
                {/* Tone-setting message */}
                <div className="mb-6 p-4 rounded-xl bg-card/50 border border-border/50 flex items-start gap-3">
                  <Heart size={14} className="text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Our memberships are designed around care, continuity and prevention — not sales. Explore what may be right for you.
                  </p>
                </div>

                <p className="text-xs text-muted-foreground tracking-widest uppercase mb-4">MEMBERSHIP PROGRAMMES</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {membershipCategories.map((cat, idx) => {
                    const IconComp = iconMap[cat.icon] || Activity;
                    const colors = colorMap[cat.color] || colorMap.blue;
                    return (
                      <motion.button
                        key={cat.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.07 }}
                        whileHover={{ y: -3, scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleCategorySelect(cat)}
                        className="premium-option-card p-5 flex items-center gap-4 text-left w-full"
                        aria-label={cat.label}
                      >
                        <div className={`option-icon-wrap ${colors.bg} ${colors.border} border`}>
                          <IconComp size={20} className={colors.icon} aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm text-foreground tracking-wide">{cat.label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{cat.forWhom}</p>
                          <p className="text-xs text-muted-foreground/70 mt-0.5 leading-relaxed">{cat.description}</p>
                        </div>
                        <ChevronRight size={16} className="text-muted-foreground flex-shrink-0" />
                      </motion.button>
                    );
                  })}
                </div>

                <div className="mt-8 p-4 rounded-xl bg-card/40 border border-border/40 flex items-start gap-3">
                  <Info size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground/80 leading-relaxed">
                    Membership details, benefits and pricing will be updated with verified All Is Well information. Contact your nearest centre for current membership options.
                  </p>
                </div>
              </motion.div>
            )}

            {/* STEP: Membership detail */}
            {step === 'detail' && selectedCategory && plan && (
              <motion.div
                key="detail"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
              >
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm mb-6"
                  aria-label="Go back to membership categories"
                >
                  <ArrowLeft size={14} />
                  <span className="text-xs tracking-widest uppercase">All Memberships</span>
                </button>

                {/* Membership card */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card border border-border rounded-2xl overflow-hidden mb-4"
                >
                  {/* Card header */}
                  <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border-b border-border p-6">
                    <div className="flex items-center gap-3 mb-3">
                      {(() => {
                        const IconComp = iconMap[selectedCategory.icon] || Activity;
                        const colors = colorMap[selectedCategory.color] || colorMap.blue;
                        return (
                          <div className={`w-12 h-12 rounded-xl ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                            <IconComp size={22} className={colors.icon} aria-hidden="true" />
                          </div>
                        );
                      })()}
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <Star size={11} className="text-primary" />
                          <span className="text-xs text-primary font-semibold tracking-widest uppercase">All Is Well</span>
                        </div>
                        <h2 className="text-xl font-bold text-white">{plan.name}</h2>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">{plan.forWhom}</p>
                  </div>

                  {/* Card body */}
                  <div className="p-6">
                    {/* Benefits */}
                    <div className="mb-5">
                      <p className="text-xs text-muted-foreground tracking-widest uppercase mb-3">MEMBERSHIP BENEFITS</p>
                      {plan.benefits.length > 0 ? (
                        <ul className="space-y-2">
                          {plan.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-foreground">
                              <ShieldCheck size={13} className="text-primary flex-shrink-0 mt-0.5" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="p-4 rounded-xl bg-muted/30 border border-border/50">
                          <p className="text-sm text-muted-foreground text-center">
                            Membership benefits will be updated with verified All Is Well information.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Validity */}
                    {plan.validity && (
                      <div className="mb-5">
                        <p className="text-xs text-muted-foreground tracking-widest uppercase mb-2">VALIDITY</p>
                        <p className="text-sm text-foreground">{plan.validity}</p>
                      </div>
                    )}

                    {/* Participating centres */}
                    <div className="mb-5">
                      <p className="text-xs text-muted-foreground tracking-widest uppercase mb-2">PARTICIPATING CENTRES</p>
                      {plan.participatingCentreIds.length > 0 ? (
                        <div className="flex flex-col gap-2">
                          {plan.participatingCentreIds.map((centreId) => {
                            const centre = centres.find((c) => c.id === centreId);
                            return centre ? (
                              <div key={centreId} className="flex items-center gap-2 text-sm text-foreground">
                                <MapPin size={12} className="text-primary flex-shrink-0" />
                                {centre.name}
                              </div>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Information coming soon</p>
                      )}
                    </div>

                    {/* Prototype notice */}
                    {!plan.isVerified && (
                      <div className="p-3 rounded-xl bg-amber-950/30 border border-amber-900/40 flex items-start gap-2 mb-5">
                        <Info size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-400/80 leading-relaxed">
                          Membership details are being finalised. Contact All Is Well directly for current membership options and pricing.
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Contact CTAs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a
                    href={hospitalConfig.phoneTel}
                    className="flex items-center justify-center gap-2 btn-primary text-xs px-4 py-3"
                    aria-label="Call All Is Well about membership"
                  >
                    <Phone size={14} />
                    CONTACT CENTRE
                  </a>
                  <a
                    href={hospitalConfig.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 btn-ghost-white text-xs px-4 py-3"
                    aria-label="WhatsApp All Is Well about membership"
                  >
                    <MessageCircle size={14} />
                    WHATSAPP
                  </a>
                </div>

                <p className="text-xs text-muted-foreground/60 text-center mt-4 leading-relaxed px-4">
                  Our team will guide you through the right membership for your care needs.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}
